import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Link,
    InputAdornment,
    Typography,
    IconButton,
    Chip,
    DialogActions,
} from '@mui/material';
import { Close as CloseIcon, ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { reportService } from '../../../services/api';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { formatDate } from '../../../utils/dateFormatter';
import EmailIcon from '@mui/icons-material/Email';
import { useToast } from '../../../context/ToastContext';
import ResponsiveTable from '../../common/ResponsiveTable';
import { tokens } from '../../../theme';
import InsightsFilterLine from '../../Insights/InsightsFilterLine';
import InsightsPager from '../../Insights/InsightsPager';

// Search Intelligence's table look on the shared table (N5 pt 4): its header type, cell type and spacing, hairline rows,
// no grey band on every other row and no row highlight. Phones keep the shared table's cards (K2.3 pt 13).
const INSIGHTS_TABLE_LOOK = {
    '& .responsive-table': { border: 'none', boxShadow: 'none', borderRadius: 0, background: 'transparent' },
    '& .responsive-table-desktop .MuiTableCell-head': { fontSize: '11px', color: tokens.color.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '9px 18px', fontWeight: 400, lineHeight: 'normal', whiteSpace: 'nowrap', borderBottom: `0.5px solid ${tokens.color.borderTertiary}`, background: tokens.color.bgPrimary, fontFamily: tokens.font.family },
    '& .responsive-table-desktop .MuiTableCell-body': { fontSize: '13px', color: tokens.color.textPrimary, padding: '11px 18px', lineHeight: 'normal', borderBottom: `0.5px solid ${tokens.color.borderTertiary}`, fontFamily: tokens.font.family },
    '& .responsive-table-desktop .MuiTableRow-hover:hover': { backgroundColor: 'transparent' },
};

// Compliance pill thresholds — mirrors the screenshot: <70 alert, 70-89 warn, >=90 positive.
const getComplianceTone = (value, T) => {
    if (value >= 90) return { bg: T.color.status.activeBg, text: T.color.status.activeText };
    if (value >= 70) return { bg: T.color.status.warnBg, text: T.color.status.warnText };
    return { bg: T.color.status.alertBg, text: T.color.status.alertText };
};

const ComplianceReport = () => {
    // The audience and cohort inherited from Insights (N5 pts 1, 30); the page has no control of its own for them.
    const [searchParams] = useSearchParams();
    // Opened from Insights (/insights/compliance), it shows Insights' filters in one line (N5 pt 1); from the admin home's
    // alert or the Reports chart (/reports/compliance) its filters are not Insights', so there is no line.
    const fromInsights = useLocation().pathname.startsWith('/insights/');
    const audience = searchParams.get('audience') || 'all';
    const cohort = searchParams.get('cohort') || 'all';
    const [page, setPage] = useState(0);
    // Ten courses a page, under the Insights pages' one pager (N5 pt 4). Removed 2 Nov 2026 (N5 pt 4): the rows-per-page
    // choice (10, 25 or 100), which the other Insights pages don't have.
    const rowsPerPage = 10;
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({completed: 0, pending: 0, overdue: 0, percentage: 0, dueSoon: 0});
    // The 'Due in N days' window: 7, 14 or 30 days, starting at 7, the window Needs Attention uses (N5 pt 33, K3 pt 9).
    // It changes only its own column and figure; Completed, Pending and Overdue stay as they are.
    const [dueWithin, setDueWithin] = useState(7);
    const DUE_WINDOWS = [7, 14, 30];
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ search: '' });
    const [searchTimeout, setSearchTimeout] = useState(null);
    const { showToast } = useToast();
    const T = tokens;
    const [learnerDialog, setLearnerDialog] = useState({
        open: false,
        data: [],
        title: '',
        courseId: null
    });
    // The pop-up's status: learners due within the window are pending (N5 pt 33).
    const dialogStatus = learnerDialog.type === 'overdue' ? 'Overdue'
        : (learnerDialog.type === 'pending' || learnerDialog.type === 'due') ? 'Pending' : 'Completed';
    const [sendingEmail, setSendingEmail] = useState(false);
    // Most overdue first; Completed, Pending and Overdue re-sort on the server across every page (N5 pts 6, 30), with
    // a 'Sorted by [column]' label over the table.
    const [sort, setSort] = useState({ id: 'overduecount', direction: 'desc' });
    const SORT_PARAM = { completedcount: 'completed', pendingcount: 'pending', overduecount: 'overdue' };
    const SORT_LABEL = { completedcount: 'Completed', pendingcount: 'Pending', overduecount: 'Overdue' };
    // Only the latest request's answer is shown: an older one that comes back later is dropped.
    const latestRequest = useRef(0);

    // A new order starts again from the first page.
    const handleSort = (columnId) => {
        setSort((prev) => {
            if (prev.id === columnId) {
                return { id: columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { id: columnId, direction: 'desc' };
        });
        setPage(0);
    };

    const sortableHeader = (label, columnId) => (
        <Box
            component="span"
            onClick={() => handleSort(columnId)}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                userSelect: 'none',
                color: sort.id === columnId ? T.color.textPrimary : 'inherit',
                fontWeight: sort.id === columnId ? T.font.weight.medium : 'inherit',
                '&:hover': { color: T.color.textPrimary },
            }}
        >
            {label}
            {sort.id === columnId && (
                sort.direction === 'desc'
                    ? <ArrowDownwardIcon sx={{ fontSize: '13px' }} />
                    : <ArrowUpwardIcon sx={{ fontSize: '13px' }} />
            )}
        </Box>
    );

    const columns = [
        {
            id: 'coursename',
            label: 'Name',
            minWidth: 190,
            format: (value) => (
                <Typography sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>
                    {value}
                </Typography>
            )
        },
        { id: 'type', label: 'Type', minWidth: 100 },
        // On one line, as Search Intelligence's dates (N5 pt 4).
        { id: 'deadlinepolicy', label: 'Deadline policy', minWidth: 140, format: (value) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{value ?? '-'}</Box> },
        {
            id: 'completedcount',
            label: sortableHeader('Completed', 'completedcount'),
            minWidth: 100,
            format: (value, row) => (
                <Link
                    component="button"
                    underline="none"
                    sx={{ color: T.color.textPrimary, fontWeight: T.font.weight.regular, '&:hover': { color: T.color.brand } }}
                    onClick={() => handleShowLearners(row.id, 'completed', 'Completed Learners')}
                >
                    {value}
                </Link>
            )
        },
        {
            id: 'pendingcount',
            label: sortableHeader('Pending', 'pendingcount'),
            minWidth: 100,
            format: (value, row) => (
                <Link
                    component="button"
                    underline="none"
                    sx={{ color: T.color.textPrimary, fontWeight: T.font.weight.regular, '&:hover': { color: T.color.brand } }}
                    onClick={() => handleShowLearners(row.id, 'pending', 'Pending Learners')}
                >
                    {value}
                </Link>
            )
        },
        {
            id: 'overduecount',
            label: sortableHeader('Overdue', 'overduecount'),
            minWidth: 100,
            format: (value, row) => (
                <Link
                    component="button"
                    underline="none"
                    sx={{
                        color: value > 0 ? T.color.status.alertText : T.color.textPrimary,
                        fontWeight: value > 0 ? T.font.weight.medium : T.font.weight.regular,
                        '&:hover': { color: T.color.brand }
                    }}
                    onClick={() => handleShowLearners(row.id, 'overdue', 'Overdue Learners')}
                >
                    {value}
                </Link>
            )
        },
        {
            // Not complete and due within the window, per course; a click lists those learners with their due dates
            // (N5 pt 33).
            id: 'duesooncount',
            label: `Due in the next ${dueWithin} days`, // 'Due in the next N days' (N5 pt 33)
            minWidth: 110,
            format: (value, row) => (
                <Link
                    component="button"
                    underline="none"
                    sx={{ color: T.color.textPrimary, fontWeight: T.font.weight.regular, '&:hover': { color: T.color.brand } }}
                    onClick={() => handleShowLearners(row.id, 'due', `Due in the next ${dueWithin} days`)}
                >
                    {value}
                </Link>
            )
        },
        {
            id: 'compliancepercentage',
            label: 'Compliance',
            minWidth: 120,
            format: value => {
                if (value === null || value === undefined) return '-'; // no enrolments for these filters
                const tone = getComplianceTone(value, T);
                return (
                    <Chip
                        label={`${value}%`}
                        size="small"
                        sx={{
                            backgroundColor: tone.bg,
                            color: tone.text,
                            fontWeight: T.font.weight.medium,
                            borderRadius: T.radius.pill,
                            height: 24,
                        }}
                    />
                );
            }
        }
    ];

    const fetchData = async () => {
        const request = ++latestRequest.current;
        try {
            setLoading(true);
            const response = await reportService.getComplianceReport({
                page,
                limit: rowsPerPage,
                search: filters.search,
                audience,
                cohort,
                sort: SORT_PARAM[sort.id] || 'overdue',
                order: sort.direction,
                dueWithin,
            });
            if (request !== latestRequest.current) return; // a newer request is on its way

            if (response.data.success) {
                    // The compliance percentage comes from the server: completed by the due date over all enrolments
                    // (K6.1 pt 5). Removed 12 Oct 2026 (K6.1 pt 5): completed over completed, pending and overdue
                    // worked out here, with a late completion in none of them.
                    const rows = response.data.data.map(row => ({
                        ...row,
                        type: row.mandatorycourse ? 'Required' : 'Optional',
                        deadlinepolicy: row.deadlinepolicy === 'days_from_enrolment' ? row.deadlinedays + ' Days from Enrollment' : row.deadlinepolicy === 'fixed' ? 'fixed. '+formatDate(row.deadline) : '-', // 'Enrollment', American English on every screen (K2.3 pt 17)
                    }));

                setData(rows);
                setSummary(response.data.summary || {
                    completed: 0,
                    pending: 0,
                    overdue: 0,
                    percentage: 0,
                    dueSoon: 0
                });
                setTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching compliance report:', error);
        } finally {
            if (request === latestRequest.current) setLoading(false);
        }
    };

    const handleShowLearners = async (courseId, type, title) => {
        try {
            const response = await reportService.getComplianceLearners(courseId, type, { audience, cohort, dueWithin });
            if (response.data.success) {
                setLearnerDialog({
                    open: true,
                    data: response.data.data,
                    emailEnabled: response.data.isEmailEnabled,
                    title,
                    courseId,
                    type
                });
            }
        } catch (error) {
            console.error('Error fetching learners:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, sort, audience, cohort, dueWithin]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        setSearchTimeout(
            setTimeout(() => {
                setPage(0);
                fetchData();
            }, 500)
        );
    }, [filters.search]);

    const handleExport = async () => {
        try {
            // The file follows the page: search, audience, cohort, order and the 'Due in N days' window (N5 pts 32, 33).
            const response = await reportService.exportComplianceReport({
                search: filters.search,
                audience,
                cohort,
                sort: SORT_PARAM[sort.id] || 'overdue',
                order: sort.direction,
                dueWithin,
            });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `compliance-report-${new Date().toISOString()}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting data:', error);
        }
    };

    const handleExportLearners = () => {
        const headers = ['Name', 'Email', 'Audience', 'Enrolled Date', 'Due Date', 'Status', 'Completion Date'];
        const statusLabel = dialogStatus;
        // The guard of the page's export (server/utils/csv.js, csvSafe): a value starting with =, +, - or @ is written
        // with a ' in front, so a spreadsheet does not run it as a formula; the dash of an empty value stays a dash.
        const escapeCsv = (val) => {
            let str = val === null || val === undefined ? '' : String(val);
            if (str !== '-' && /^[=+\-@]/.test(str)) str = `'${str}`;
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };

        // Every column of the pop-up; an empty value is a dash (N5 pt 32).
        const rows = learnerDialog.data.map(learner => ([
            [learner.first_name, learner.last_name].filter(Boolean).join(' ') || '-',
            learner.email || '-',
            learner.audiencetype === 1 ? 'Internal' : learner.audiencetype === 2 ? 'External' : '-',
            learner.date_enrolled ? formatDate(learner.date_enrolled) : '-',
            learner.duedate ? formatDate(learner.duedate) : '-',
            statusLabel,
            learner.date_completed ? formatDate(learner.date_completed) : '-',
        ]));

        const csvContent = [headers, ...rows]
            .map(row => row.map(escapeCsv).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeTitle = (learnerDialog.title || 'learners').toLowerCase().replace(/[^a-z0-9]+/g, '-');
        a.download = `${safeTitle}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleSendEscalation = async () => {
        try {
            setSendingEmail(true);
            const learnerIds = learnerDialog.data.map(learner => learner.id);
            const response = await reportService.sendOverdueEscalation(
                learnerDialog.courseId,
                learnerIds
            );

            if (response.data.success) {
                showToast('Escalation emails sent successfully','success');
            }
        } catch (error) {
            console.error('Error sending escalation emails:', error);
            showToast('Failed to send escalation emails','error');
        } finally {
            setSendingEmail(false);
        }
    };

    // Removed 12 Oct 2026 (N5 pt 30): sorting only the current page's rows in the browser; the server sorts every page.

    const summaryTotal = summary.completed + summary.pending + summary.overdue;

    // One look across the Insights pages, based on Search Intelligence (N5 pt 4): its heading, its framed sections with
    // a title and 'Sorted by' over the table, its table type and spacing, and its pager. Removed 2 Nov 2026 (N5 pt 4):
    // the page's own padding, the large page title, the raised cards, the grey band on every other row and the
    // uppercase 'By course · Sorted by' label.
    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ marginBottom: '24px' }}>
                <Typography sx={{ fontSize: T.font.size.xl, fontWeight: T.font.weight.medium, color: T.color.textPrimary, letterSpacing: '-0.02em', fontFamily: T.font.family }}>
                    Compliance Report
                </Typography>
                {/* The audience and cohort from Insights in one read-only line; the page has no period (N5 pt 1). Removed
                    2 Nov 2026 (N5 pt 1): an empty line of text in its place. */}
                {fromInsights && <InsightsFilterLine audience={audience} cohort={cohort} withPeriod={false} />}
            </Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Course"
                        className="pink-filter"
                        sx={{ height: '35px' }}
                        variant="outlined"
                        size="small"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {/* The 'Due in the next 7 / 14 / 30 days' control (N5 pt 33): 'Due in the next [7] [14] [30] days'.
                        Removed 2 Nov 2026 (N5 pt 33): 'Due in [7] [14] [30] days', here, on the column, the summary, the
                        pop-up's title and the export. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary }}>Due in the next</Typography>
                        {DUE_WINDOWS.map((days) => (
                            <Box
                                key={days}
                                role="button"
                                onClick={() => setDueWithin(days)}
                                sx={{
                                    fontSize: T.font.size.xs,
                                    color: dueWithin === days ? T.color.textPrimary : T.color.textSecondary,
                                    background: dueWithin === days ? T.color.bgSecondary : 'transparent',
                                    border: `0.5px solid ${dueWithin === days ? T.color.borderSecondary : T.color.borderTertiary}`,
                                    borderRadius: T.radius.pill,
                                    padding: '4px 12px',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                {days}
                            </Box>
                        ))}
                        <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary }}>days</Typography>
                    </Box>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                >
                    Export
                </Button>
            </Box>

            {/* ── Compliance overview card ───────────────────────────────────── */}
            <Box
                sx={{
                    mb: '10px',
                    p: '18px',
                    background: T.color.bgPrimary,
                    border: `0.5px solid ${T.color.borderTertiary}`,
                    borderRadius: T.radius.lg,
                }}
            >
                <Typography
                    sx={{
                        fontSize: T.font.size.xs,
                        color: T.color.textSecondary,
                        letterSpacing: T.font.letterSpacing.label,
                        textTransform: 'uppercase',
                        mb: 2,
                    }}
                >
                    Compliance overview
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                    }}
                >
                    {/* Big percentage */}
                    <Box sx={{ minWidth: '120px' }}>
                        <Typography
                            sx={{
                                fontSize: '40px',
                                fontWeight: 700,
                                lineHeight: 1,
                                color: T.color.status.activeText,
                            }}
                        >
                            {summary.percentage === null || summary.percentage === undefined ? '-' : `${summary.percentage}%`}
                        </Typography>
                        <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, mt: 1 }}>
                            overall compliant
                        </Typography>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '0.5px', alignSelf: 'stretch', backgroundColor: T.color.borderTertiary, display: { xs: 'none', md: 'block' } }} />

                    {/* Bar + legend */}
                    <Box sx={{ flex: 1, minWidth: '260px' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                height: '30px',
                                borderRadius: T.radius.sm,
                                overflow: 'hidden',
                                background: T.color.bgTertiary,
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${summaryTotal ? (summary.completed / summaryTotal) * 100 : 0}%`,
                                    background: T.color.status.activeText,
                                }}
                            />
                            <Box
                                sx={{
                                    width: `${summaryTotal ? (summary.pending / summaryTotal) * 100 : 0}%`,
                                    background: T.color.warning,
                                }}
                            />
                            <Box
                                sx={{
                                    width: `${summaryTotal ? (summary.overdue / summaryTotal) * 100 : 0}%`,
                                    background: T.color.status.alertText,
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: T.color.status.activeText }} />
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Completed <Box component="span" sx={{ color: T.color.textSecondary }}>{summary.completed}</Box>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: T.color.warning }} />
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Pending <Box component="span" sx={{ color: T.color.textSecondary }}>{summary.pending}</Box>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: T.color.status.alertText }} />
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Overdue <Box component="span" sx={{ color: T.color.textSecondary }}>{summary.overdue}</Box>
                                </Typography>
                            </Box>
                            {/* The table's 'Due in N days' figure for every course (N5 pt 33); part of Pending, so not in the bar. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Due in the next {dueWithin} days <Box component="span" sx={{ color: T.color.textSecondary }}>{summary.dueSoon ?? 0}</Box>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── By course table card ───────────────────────────────────────── */}
            <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, marginBottom: '10px', overflow: 'hidden', width: '100%', ...INSIGHTS_TABLE_LOOK }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `0.5px solid ${T.color.borderTertiary}`, flexWrap: 'wrap', gap: '6px' }}>
                    <Box component="span" sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>By course</Box>
                    {/* The order the table is in (N5 pt 6). Removed 12 Oct 2026 (N5 pt 6): 'Sortable columns', which did
                        not say the order. */}
                    <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>
                        Sorted by {SORT_LABEL[sort.id] || 'Overdue'}
                    </Typography>
                </Box>
                <ResponsiveTable
                    columns={columns}
                    rows={data}
                    loading={loading}
                    emptyTitle="No compliance records found"
                    renderCell={(column, row) => {
                        const value = row[column.id];

                        if (column.format && typeof value !== 'undefined') {
                            return column.format(value, row);
                        }

                        if (column.id === 'deadline') {
                            return value ? formatDate(value) : '-';
                        }

                        return value ?? '-';
                    }}
                />
                {/* The Insights pages' pager (N5 pt 4); it counts pages from 1, the page state from 0. */}
                {total > rowsPerPage && <InsightsPager page={page + 1} total={total} perPage={rowsPerPage} onChange={(p) => setPage(p - 1)} />}
            </Box>

            <Dialog
                open={learnerDialog.open}
                onClose={() => setLearnerDialog({ ...learnerDialog, open: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">{learnerDialog.title}</Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => setLearnerDialog({ ...learnerDialog, open: false })}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <ResponsiveTable
                        columns={[
                            { id: 'name', label: 'Name' },
                            { id: 'email', label: 'Email' },
                            { id: 'audienceType', label: 'Audience' },
                            { id: 'date_enrolled', label: 'Enrolled Date' },
                            { id: 'duedate', label: 'Due date' },
                            { id: 'dueStatus', label: 'Status' },
                            { id: 'date_completed', label: 'Completion Date' },
                        ]}
                        rows={learnerDialog.data}
                        emptyTitle="No learners found"
                        renderCell={(column, learner) => {
                            if (column.id === 'name') {
                                return `${learner.first_name} ${learner.last_name}`;
                            }

                            if (column.id === 'audienceType') {
                                return learner.audiencetype === 1 ? 'Internal' : learner.audiencetype === 2 ? 'External' : '-';
                            }

                            if (column.id === 'duedate') {
                                return learner.duedate ? formatDate(learner.duedate) : '-';
                            }

                            if (column.id === 'dueStatus') {
                                return <Chip
                                    label={dialogStatus}
                                    size="small"
                                    sx={{
                                        backgroundColor: dialogStatus === 'Overdue' ? T.color.status.alertBg :
                                        dialogStatus === 'Pending' ? T.color.status.warnBg : T.color.status.activeBg,
                                        borderRadius: 8,
                                        color: dialogStatus === 'Overdue' ? T.color.status.alertText :
                                        dialogStatus === 'Pending' ? T.color.status.warnText : T.color.status.activeText,
                                        fontWeight: 600,
                                        height: 24,
                                    }}
                                />
                            }

                            if (column.id === 'date_enrolled') {
                                return formatDate(learner.date_enrolled);
                            }

                            if (column.id === 'date_completed') {
                                return learner.date_completed
                                    ? formatDate(learner.date_completed)
                                    : '-';
                            }

                            return learner[column.id];
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        {learnerDialog.title === 'Overdue Learners'  && learnerDialog.emailEnabled == 1 ? (
                            <Button
                                variant="contained"
                                disabled={sendingEmail}
                                onClick={handleSendEscalation}
                                startIcon={<EmailIcon />}
                                sx={{ backgroundColor: T.color.status.alertText, color:T.color.status.alertBg}}
                            >
                                {sendingEmail ? 'Sending...' : 'Send Escalation'}
                            </Button>
                        ) : <Box />}
                        <Button
                            variant="outlined"
                            disabled={!learnerDialog.data || learnerDialog.data.length === 0}
                            onClick={handleExportLearners}
                            startIcon={<FileDownloadIcon />}
                        >
                            Export this list
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ComplianceReport;